import React, { createContext, useState, type ReactNode } from 'react';
import { View, type LayoutChangeEvent } from 'react-native';
import { useScreenSize } from '@banhala/ui-kit';

export type ScreenSize = { width: number; height: number };

export const ScreenSizeContext = createContext<ScreenSize | null>(null);

/**
 * 화면 크기를 layout 측정 기반으로 제공하는 Provider.
 *
 * 브라운필드 RN bridge 부트스트랩 race로 `Dimensions` 캐시가 0인 케이스에서
 * `useWindowDimensions()`가 0을 반환할 수 있는 문제를 우회한다.
 * View의 `onLayout`은 캐시와 무관하게 실제 layout 결과를 제공하므로 안전하다.
 */
export const ScreenSizeProvider = ({ children }: { children: ReactNode }) => {
  const fallback = useScreenSize();

  const [size, setSize] = useState<ScreenSize>({
    width: fallback.width,
    height: fallback.height,
  });

  const handleLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    if (width <= 0 || height <= 0) return;
    setSize(prev =>
      prev.width === width && prev.height === height ? prev : { width, height },
    );
  };

  return (
    // flex:1만으로는 web에서 부모가 flex container가 아닐 때 expand가 안 되므로
    // width:'100%'를 함께 두어 native/web 모두 layout이 부모를 채우도록 함.
    <View
      style={{ flex: 1, width: '100%', height: '100%' }}
      onLayout={handleLayout}
    >
      <ScreenSizeContext.Provider value={size}>
        {children}
      </ScreenSizeContext.Provider>
    </View>
  );
};
